import { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  AvatarGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Skeleton,
} from '@mui/material';
import {
  Add,
  Sort,
  MoreVert,
  Folder,
  Edit,
  Delete,
  FolderOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import DesignSystem from '../designSystem';
import { useAuth } from '../contexts/AuthContext';
import { getApiLanguage } from '../config/apiLanguage';
import {
  fetchUserFolders,
  createFolder,
  renameFolder,
  deleteFolder,
  ApiFolder,
  getBusinessLogoUrl,
} from '../services/api';
import { displayFolderName } from '../utils/folderDisplayName';
import { EmptyState, EntityCard, EntityRow, PageHeader } from '../components/ui';
import { useT } from '../i18n/useT';

interface FolderItem {
  id: string;
  name: string;
  createdAt: string;
  businessCount: number;
  businesses: {
    id: string;
    name: string;
    logo?: string;
  }[];
}

type SortOption = 'name' | 'date' | 'count';

export default function BookmarksPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const t = useT();
  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortOption, setSortOption] = useState<SortOption>('date');
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(null);
  const [folderMenuAnchor, setFolderMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editFolderName, setEditFolderName] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  // Load folders from API
  useEffect(() => {
    const loadFolders = async () => {
      if (!user?.token) {
        setIsLoading(false);
        return;
      }

      try {
        const apiSort = sortOption === 'name' ? 'name' : 'new';
        const response = await fetchUserFolders(user.token, getApiLanguage(), apiSort);
        if (response.folders) {
          const mappedFolders: FolderItem[] = response.folders.map((folder: ApiFolder) => ({
            id: folder.id.toString(),
            name: folder.name,
            createdAt: folder.created_at || new Date().toISOString().split('T')[0],
            businessCount: folder.business_count || folder.businesses?.length || 0,
            businesses: (folder.businesses || []).slice(0, 4).map((b) => ({
              id: b.id.toString(),
              name: b.name,
              logo: b.logo ? getBusinessLogoUrl(b.logo) || undefined : undefined,
            })),
          }));
          setFolders(mappedFolders);
        }
      } catch (error) {
        console.error('Error loading folders:', error);
      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    loadFolders();
  }, [user?.token, sortOption]);

  const handleSortMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSortMenuAnchor(event.currentTarget);
  };

  const handleSortMenuClose = () => {
    setSortMenuAnchor(null);
  };

  const handleSortChange = (option: SortOption) => {
    setSortOption(option);
    handleSortMenuClose();
  };

  const handleFolderMenuOpen = (event: React.MouseEvent<HTMLElement>, folderId: string) => {
    event.stopPropagation();
    setFolderMenuAnchor(event.currentTarget);
    setSelectedFolderId(folderId);
  };

  const handleFolderMenuClose = () => {
    setFolderMenuAnchor(null);
    setSelectedFolderId(null);
  };

  const handleCreateFolder = async () => {
    if (newFolderName.trim() && user?.token) {
      setActionError(null);
      try {
        await createFolder(newFolderName.trim(), user.token, undefined, getApiLanguage());
        const apiSort = sortOption === 'name' ? 'name' : 'new';
        const refreshed = await fetchUserFolders(user.token, getApiLanguage(), apiSort);
        const mappedFolders: FolderItem[] = (refreshed.folders || []).map((folder: ApiFolder) => ({
          id: folder.id.toString(),
          name: folder.name,
          createdAt: folder.created_at || new Date().toISOString().split('T')[0],
          businessCount: folder.business_count || folder.businesses?.length || 0,
          businesses: (folder.businesses || []).slice(0, 4).map((b) => ({
            id: b.id.toString(),
            name: b.name,
            logo: b.logo ? getBusinessLogoUrl(b.logo) || undefined : undefined,
          })),
        }));
        setFolders(mappedFolders);
        setNewFolderName('');
        setCreateDialogOpen(false);
      } catch (error) {
        console.error('Error creating folder:', error);
        setActionError(error instanceof Error ? error.message : 'Could not create this list right now.');
      }
    }
  };

  const handleEditFolder = async () => {
    if (editFolderName.trim() && selectedFolderId && user?.token) {
      setActionError(null);
      try {
        await renameFolder(parseInt(selectedFolderId), editFolderName.trim(), user.token, getApiLanguage());
        setFolders(folders.map(f =>
          f.id === selectedFolderId ? { ...f, name: editFolderName.trim() } : f
        ));
        setEditDialogOpen(false);
        setEditFolderName('');
        setSelectedFolderId(null);
      } catch (error) {
        console.error('Error renaming folder:', error);
        setActionError(error instanceof Error ? error.message : 'Could not rename this list right now.');
      }
    }
  };

  const handleDeleteFolder = async () => {
    if (selectedFolderId && user?.token) {
      setActionError(null);
      try {
        await deleteFolder(parseInt(selectedFolderId), user.token, getApiLanguage());
        setFolders(folders.filter(f => f.id !== selectedFolderId));
        setDeleteDialogOpen(false);
        setSelectedFolderId(null);
      } catch (error) {
        console.error('Error deleting folder:', error);
        setActionError(error instanceof Error ? error.message : 'Could not delete this list right now.');
      }
    }
  };

  const openEditDialog = () => {
    const folder = folders.find(f => f.id === selectedFolderId);
    if (folder) {
      setEditFolderName(folder.name);
      setEditDialogOpen(true);
    }
    handleFolderMenuClose();
  };

  const openDeleteDialog = () => {
    setDeleteDialogOpen(true);
    handleFolderMenuClose();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const sortedFolders = [...folders].sort((a, b) => {
    switch (sortOption) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'date':
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case 'count':
        return b.businessCount - a.businessCount;
      default:
        return 0;
    }
  });

  const getSortLabel = () => {
    switch (sortOption) {
      case 'name': return t('bookmarksSortLabelAz');
      case 'date': return t('bookmarksSortLabelDate');
      case 'count': return t('bookmarksSortLabelCount');
    }
  };

  const listCountLabel = sortedFolders.length === 1
    ? t('bookmarksListCountOne', { n: sortedFolders.length })
    : t('bookmarksListCountMany', { n: sortedFolders.length });

  return (
    <AppShell title={t('navLists')} hideTitle maxWidth="feed" showCompanion>
        <PageHeader
          title={t('navLists')}
          subtitle={sortedFolders.length ? listCountLabel : undefined}
          actions={
            <>
              <IconButton
                onClick={() => setCreateDialogOpen(true)}
                aria-label={t('bookmarksCreateListAria')}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: DesignSystem.borderRadius.pill,
                  bgcolor: DesignSystem.colors.surface,
                  color: DesignSystem.colors.textPrimary,
                  '&:hover': { bgcolor: DesignSystem.colors.surfaceElevated },
                }}
              >
                <Add />
              </IconButton>
              <Button
                onClick={handleSortMenuOpen}
                endIcon={<Sort />}
                size="small"
                sx={{
                  color: DesignSystem.colors.textPrimary,
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: DesignSystem.borderRadius.pill,
                  bgcolor: DesignSystem.colors.surface,
                  px: 1.5,
                  '&:hover': { bgcolor: DesignSystem.colors.surfaceElevated },
                }}
              >
                {getSortLabel()}
              </Button>
              <Menu
                anchorEl={sortMenuAnchor}
                open={Boolean(sortMenuAnchor)}
                onClose={handleSortMenuClose}
                PaperProps={{ sx: { borderRadius: DesignSystem.borderRadius.md, minWidth: 150 } }}
              >
                <MenuItem onClick={() => handleSortChange('name')} selected={sortOption === 'name'}>
                  {t('bookmarksSortNameAz')}
                </MenuItem>
                <MenuItem onClick={() => handleSortChange('date')} selected={sortOption === 'date'}>
                  {t('bookmarksSortDateCreated')}
                </MenuItem>
                <MenuItem onClick={() => handleSortChange('count')} selected={sortOption === 'count'}>
                  {t('bookmarksSortBusinessCount')}
                </MenuItem>
              </Menu>
            </>
          }
        />

        {actionError && (
          <Box sx={{ mb: 1.5, px: 0.5, color: DesignSystem.colors.error, fontSize: 14 }}>
            {actionError}
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={64} sx={{ bgcolor: DesignSystem.colors.surface }} />
            ))
          ) : sortedFolders.length === 0 ? (
            <EmptyState
              icon={<Folder />}
              title={t('bookmarksNoListsTitle')}
              body={t('bookmarksNoListsBody')}
              actionLabel={t('bookmarksCreateList')}
              onAction={() => setCreateDialogOpen(true)}
            />
          ) : (
            sortedFolders.map((folder, index) => (
              <EntityCard key={folder.id} pad={false}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <EntityRow
                      dense
                      onClick={() => navigate(`/bookmarks/${folder.id}`)}
                      avatar={
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: DesignSystem.colors.surfaceElevated,
                            color: DesignSystem.colors.textSecondary,
                          }}
                        >
                          <FolderOutlined sx={{ fontSize: 20 }} />
                        </Avatar>
                      }
                      title={displayFolderName(folder.name, index)}
                      meta={`${formatDate(folder.createdAt)}${folder.businessCount ? ` · ${folder.businessCount === 1 ? t('visiCountOne', { n: folder.businessCount }) : t('visiCountMany', { n: folder.businessCount })}` : ''}`}
                      trailing={
                        folder.businesses.length > 0 ? (
                          <AvatarGroup
                            max={3}
                            sx={{
                              '& .MuiAvatar-root': {
                                width: 28,
                                height: 28,
                                fontSize: 11,
                                border: `2px solid ${DesignSystem.colors.surface}`,
                              },
                            }}
                          >
                            {folder.businesses.map((b) => (
                              <Avatar key={b.id} src={b.logo} alt={b.name}>
                                {b.name.charAt(0)}
                              </Avatar>
                            ))}
                          </AvatarGroup>
                        ) : undefined
                      }
                    />
                  </Box>
                  <IconButton
                    size="small"
                    aria-label={t('bookmarksListOptionsAria')}
                    onClick={(e) => handleFolderMenuOpen(e, folder.id)}
                    sx={{ color: DesignSystem.colors.textSecondary, mr: 0.5 }}
                  >
                    <MoreVert />
                  </IconButton>
                </Box>
              </EntityCard>
            ))
          )}
        </Box>

      {/* Folder Actions Menu */}
      <Menu
        anchorEl={folderMenuAnchor}
        open={Boolean(folderMenuAnchor)}
        onClose={handleFolderMenuClose}
        PaperProps={{ sx: {
            borderRadius: DesignSystem.borderRadius.md,
            minWidth: 150,
          } }}
      >
        <MenuItem onClick={openEditDialog}>
          <Edit sx={{ mr: 1.5, fontSize: 20 }} />
          {t('rename')}
        </MenuItem>
        <MenuItem onClick={openDeleteDialog} sx={{ color: DesignSystem.colors.error }}>
          <Delete sx={{ mr: 1.5, fontSize: 20 }} />
          {t('delete')}
        </MenuItem>
      </Menu>

      {/* Create Folder Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        PaperProps={{ sx: {
            borderRadius: DesignSystem.borderRadius.lg,
            minWidth: 320,
          } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>{t('bookmarksCreateNewList')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={t('bookmarksListName')}
            placeholder={t('bookmarksListNamePlaceholder')}
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            sx={{ mt: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleCreateFolder();
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              setNewFolderName('');
            }}
            sx={{ color: DesignSystem.colors.textSecondary }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleCreateFolder}
            variant="contained"
            disabled={!newFolderName.trim()}
          >
            {t('createVisiButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Folder Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        PaperProps={{ sx: {
            borderRadius: DesignSystem.borderRadius.lg,
            minWidth: 320,
          } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>{t('bookmarksRenameList')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={t('bookmarksListName')}
            value={editFolderName}
            onChange={(e) => setEditFolderName(e.target.value)}
            sx={{ mt: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleEditFolder();
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setEditDialogOpen(false);
              setEditFolderName('');
            }}
            sx={{ color: DesignSystem.colors.textSecondary }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleEditFolder}
            variant="contained"
            disabled={!editFolderName.trim()}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Folder Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: {
            borderRadius: DesignSystem.borderRadius.lg,
            minWidth: 300,
          } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>{t('bookmarksDeleteList')}</DialogTitle>
        <DialogContent>
          <Box sx={{ color: DesignSystem.colors.textSecondary, fontSize: 14 }}>
            {t('bookmarksDeleteListBody')}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ color: DesignSystem.colors.textSecondary }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleDeleteFolder}
            variant="contained"
            sx={{
              backgroundColor: DesignSystem.colors.error,
              '&:hover': { backgroundColor: '#d32f2f' },
            }}
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </AppShell>
  );
}
